/**
 * The user's recent Zoe conversations — pick one up where you left off, or
 * delete it.
 *
 * The delete control hover-reveals on wide screens to keep the list calm, but
 * is always visible on narrow ones — touch has no hover, and an invisible
 * control is an unusable one.
 *
 * Delete confirms inline, in the row, rather than with a browser dialog: the
 * decision stays next to the thing being deleted, and it matches how the rest
 * of the product asks. It is permanent — the conversation and everything
 * recorded from it — so it always asks.
 */

import { useState } from 'react';
import { MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConversationSummary } from '../../types/zoeConversations';
import { relativeTime } from './relativeTime';

interface Props {
  conversations: ConversationSummary[];
  /** The conversation currently open, marked in the list. */
  activeId?: string;
  onOpen: (conversationId: string) => void;
  onDelete: (conversationId: string) => Promise<void>;
  /** Disables opening while another conversation is loading. */
  disabled?: boolean;
}

export default function ZoeConversationList({ conversations, activeId, onOpen, onDelete, disabled = false }: Props) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const confirmDelete = async (id: string) => {
    setDeleting(id);
    setFailed(null);
    try {
      await onDelete(id);
      setConfirming(null);
    } catch {
      setFailed(id);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <ul className="divide-y divide-border/60 list-none pl-0">
      {conversations.map((c) => {
        const title = c.title || c.last_question || 'Untitled conversation';
        const isActive = c.conversation_id === activeId;
        const isConfirming = confirming === c.conversation_id;
        const isDeleting = deleting === c.conversation_id;
        const messages = `${c.turn_count} message${c.turn_count === 1 ? '' : 's'}`;
        return (
          <li key={c.conversation_id} className="group flex items-center gap-2 px-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpen(c.conversation_id)}
              disabled={disabled || isDeleting}
              aria-current={isActive ? 'true' : undefined}
              className={`flex min-w-0 flex-1 h-auto items-start gap-2.5 rounded-lg px-2 py-2.5 text-left ${
                isActive ? 'bg-ring/20 hover:bg-ring/20' : 'hover:bg-card/60'
              }`}
            >
              <MessageSquare
                className={`mt-0.5 h-4 w-4 shrink-0 ${isActive ? 'text-ring' : 'text-muted-foreground'}`}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground" title={title}>
                  {title}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {messages}
                  {c.updated_at ? ` · ${relativeTime(c.updated_at)}` : ''}
                  {isActive ? ' · open' : ''}
                </span>
              </span>
            </Button>

            {isConfirming ? (
              <div className="flex shrink-0 items-center gap-1.5" role="group" aria-label="Confirm delete">
                <span className="text-xs text-muted-foreground">{failed === c.conversation_id ? 'Failed —' : 'Delete?'}</span>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => void confirmDelete(c.conversation_id)}
                  loading={isDeleting}
                  className="h-auto gap-1 px-2 py-1 border border-destructive/50 bg-destructive/20 text-xs hover:bg-destructive/40"
                >
                  {failed === c.conversation_id ? 'Retry' : 'Delete'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setConfirming(null);
                    setFailed(null);
                  }}
                  disabled={isDeleting}
                  className="h-auto px-2 py-1 border-accent hover:border-muted-foreground hover:bg-transparent"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => setConfirming(c.conversation_id)}
                aria-label={`Delete conversation: ${title}`}
                className="transition-opacity hover:bg-card hover:text-destructive focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
